<template>
  <div :class="{ desktop: isDesktop }" class="specific-container">
    <el-select
      v-model="model.id"
      class="browser-selector"
      @update:modelValue="updateBrowser"
    >
      <el-option
        v-for="item in browserOptions"
        :key="item.key"
        :label="item.label"
        :value="item.key"
      />
    </el-select>
    <el-select
      v-model="model.comparison"
      class="comparison-selector"
      @update:modelValue="updateComparison"
    >
      <el-option
        v-for="item in comparisonOptions"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
    </el-select>
    <el-input
      v-model="model.version"
      class="version-input"
      @update:modelValue="updateVersion"
    />
    <el-button
      class="remove-button"
      icon="DeleteElementIcon"
      plain
      circle
      @click.prevent="removeSpecific"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import {
  BrowserLabels,
  BrowserLabel,
  Browser,
  SpecificVersion,
  VersionComparisonOptions,
  VersionComparison
} from '@/api/types'
import useResize from '@/hooks/useResize'

type BrowserEntry = {
  key: Browser
  label: string
  disabled: boolean
}

export default defineComponent({
  name: 'SpecificBrowserSelector',
  props: {
    browser: {
      type: Object,
      required: true
    },
    availableBrowsers: {
      type: Array,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  setup(props, context) {
    const model = ref(props.browser)
    const browserOptions = computed(() => {
      return BrowserLabels.filter(
        (label: BrowserLabel) =>
          label.type_id === props.browser.id ||
          props.availableBrowsers.indexOf(label.type_id) !== -1
      ).map<BrowserEntry>((label: BrowserLabel) => {
        return {
          key: label.type_id,
          label: label.label,
          disabled: false
        }
      })
    })

    function updateBrowser(newBrowser: Browser) {
      const newSpecific: SpecificVersion<Browser> = {
        id: newBrowser,
        version: props.browser.version,
        comparison: props.browser.comparison
      }
      context.emit('update', { index: props.index, specific: newSpecific })
    }

    function updateComparison(newComparison: VersionComparison) {
      const newSpecific: SpecificVersion<Browser> = {
        id: props.browser.id,
        version: props.browser.version,
        comparison: newComparison
      }
      context.emit('update', { index: props.index, specific: newSpecific })
    }

    function updateVersion(newVersion: string) {
      const newSpecific: SpecificVersion<Browser> = {
        id: props.browser.id,
        version: newVersion,
        comparison: props.browser.comparison
      }
      context.emit('update', { index: props.index, specific: newSpecific })
    }

    function removeSpecific() {
      context.emit('remove', props.index)
    }

    const comparisonOptions = VersionComparisonOptions

    const { isDesktop } = useResize()

    return {
      model,
      browserOptions,
      updateBrowser,
      updateComparison,
      updateVersion,
      removeSpecific,
      comparisonOptions,
      isDesktop
    }
  }
})
</script>

<style lang="scss" scoped>
.specific-container {
  margin-bottom: 0.5em;
}

.browser-selector {
  width: 120px;
}

.desktop .browser-selector {
  width: 180px;
}

.comparison-selector {
  width: 65px;
}

.desktop .comparison-selector {
  width: 70px;
  margin: 0 0.5em;
}

.version-input {
  width: 100px;
}
.desktop .version-input {
  width: 180px;
}

.remove-button {
  margin-left: 0.5em;
}
.desktop .remove-button {
  margin-left: 1.5em;
}
</style>
